import React, { useRef, useMemo, memo, useLayoutEffect } from 'react';
import { callToMain } from '@djs';
import { useDelayValue, useExternRef } from '@dui/hooks';
import { useLatest } from '@djs/react';

export interface CSSTransitionClassNames {
  willEnter: string;
  entering: string;
  entered: string;
  willExit: string;
  exiting: string;
  exited: string;
}

interface CSSTransitionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  className: string;
  /**
   * Мапа класснеймов-модификаторов состояний выполнения перехода
   */
  classNames: CSSTransitionClassNames;
  /**
   * Состояние перехода
   */
  on: boolean;
  /**
   * Длительность перехода
   */
  duration: number;
  /**
   * Отложенная смена состояния с учетом на нагрузку CPU
   * @default true
   */
  deferred?: boolean;
  customReflowTrigger?: (scrollTop: number) => void;
  children?: React.ReactNode | ((exited: boolean) => React.ReactNode);
  getRootRef?: React.Ref<HTMLDivElement>;
}

/**
 * Компонент с html div элементом, на который навешиваются классы
 * с модификаторами жизненного цикла анимации перехода между двумя состояниями.
 * Альтернатива react-transition-group.
 */
export const CSSTransition = memo(function CSSTransition(props: CSSTransitionProps) {
  const {
    classNames,
    className,
    on,
    deferred = true,
    duration,
    children,
    getRootRef,
    customReflowTrigger,
    ...restProps
  } = props;

  const mountedRef = useRef(false);
  const elRef = useExternRef(getRootRef);
  const deferredRef = useLatest(deferred);

  const child = useDelayValue(typeof children === 'function' ? children(!on) : children, [on], on ? 0 : duration);

  const classNamesStrings = useMemo(
    () => ({
      enter: [classNames.willEnter, classNames.entering, classNames.entered].map((x) => x.split(/\s+/).filter(Boolean)),
      exit: [classNames.willExit, classNames.exiting, classNames.exited].map((x) => x.split(/\s+/).filter(Boolean)),
    }),
    [classNames],
  );

  useLayoutEffect(() => {
    const el = elRef.current;

    if (!el) return;

    const [will, active, done] = classNamesStrings[on ? 'enter' : 'exit'];

    if (!mountedRef.current) {
      mountedRef.current = true;
      const classNameClasses = className.split(/\s+/).filter(Boolean);
      el.classList.add(...classNameClasses, ...done);

      return;
    }

    let cancelled = false;
    let endTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const animateTransition = () => {
      if (cancelled) return;

      if (customReflowTrigger) {
        const scrollTop = el.scrollTop;

        el.classList.remove(...classNamesStrings.enter.flat(2), ...classNamesStrings.exit.flat(2));
        el.classList.add(...will);

        customReflowTrigger(scrollTop);
      } else {
        el.classList.remove(...classNamesStrings.enter.flat(2), ...classNamesStrings.exit.flat(2));
        el.classList.add(...will);

        el.scrollTop; // triggers Reflow
      }

      el.classList.remove(...will);
      el.classList.add(...active);

      endTimeoutId = setTimeout(() => {
        el.classList.remove(...active);
        el.classList.add(...done);
      }, duration);
    };

    if (deferredRef.current) {
      /** Оптимизация: выносим рефлоу в отдельную таску, чтобы разбить лонгтаску и дать время на рендер */
      callToMain(animateTransition);
    } else {
      animateTransition();
    }

    return () => {
      cancelled = true;
      clearTimeout(endTimeoutId);
    };
  }, [on, deferredRef]); // eslint-disable-line

  return (
    <div ref={elRef} {...restProps}>
      {child}
    </div>
  );
});
